import { useMemo, type VoidFunctionComponent } from 'react';
import * as _ from 'lodash';

import CallToAction from '../CallToAction';
import PageHeadingBreadcrumbs from '../generic/PageHeadingBreadcrumbs';
import TeamSelectionMenu from '../shared/teams/TeamSelectionMenu';
import { window } from '../../globals';
import { useTools } from '../../hooks/api/techStack';
import ToolsTable from './ToolsTable';
import SearchBar from './SearchBar';
import { useQuery } from '../../contexts/RouterContext';
import type { GetToolsParams } from '../../../shared/techStack';

import styles from './index.module.less';

/**
 * This is the root/route component for the Tool Scanning feature.
 */
const TechStackContainer: VoidFunctionComponent = () => {
  //  useMemo helps us avoid passing down a new object on every re-render
  const crumbs = useMemo(() => ([
    {
      text: window.user?.organization?.title ?? 'Organization',
      to: '/projects',
    },
    {
      text: 'Tools',
    },
    {
      content: <TeamSelectionMenu />,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-render if the organization title changes
  ]), [window.user?.organization?.title]);

  const [query, setQuery] = useQuery<GetToolsParams>();
  const { data, isLoading, isError } = useTools({ scope: 'org' }, query);

  return (
    <>
      <CallToAction>
        <PageHeadingBreadcrumbs crumbs={crumbs} />
      </CallToAction>

      <div className="container">
        <div className={styles.header}>
          <div className={styles.search}>
            <SearchBar
              initialSearch={query.name ?? ''}
              onSearchChange={(value) => {
                if (value) {
                  setQuery({ ...query, name: value });
                } else {
                  // If the search is cleared, we want to remove the name from the params.
                  setQuery(_.omit(query, 'name'));
                }
              }}
            />
          </div>
          {/* TODO(CORE-5141): Add categories dropdown */}
          {/* TODO(CORE-5142): Add export csv button */}
        </div>

        <ToolsTable
          tools={data?.results ?? []}
          isLoading={isLoading}
          isError={isError}
        />
      </div>
    </>
  );
};

export default TechStackContainer;
